import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Leaf, Facebook, Instagram } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSiteName } from '../contexts/SiteSettingsContext';
import BrandName from './BrandName';

const API = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [config, setConfig] = useState(null);
  const { user } = useAuth();
  const siteName = useSiteName();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    axios.get(`${API}/api/cms/footer`).then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  const renderLink = (link, idx) => {
    if (!link.visible) return null;
    if (link.admin_only && !isAdmin) return null;
    if (link.external || /^https?:\/\//i.test(link.url) || link.url?.startsWith('/api/')) {
      const href = link.url?.startsWith('/api/') ? `${API}${link.url}` : link.url;
      return (
        <li key={idx}>
          <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid={`footer-link-${idx}`}>{link.label}</a>
        </li>
      );
    }
    return (
      <li key={idx}>
        <Link to={link.url} className="hover:text-accent transition-colors" data-testid={`footer-link-${idx}`}>{link.label}</Link>
      </li>
    );
  };

  if (!config) return null;
  const columns = (config.columns || []).filter((c) => c && c.title);
  const social = config.social_links || {};
  const hasSocial = !!(social.facebook_url || social.instagram_url);

  return (
    <footer className="bg-[#1A237E] text-white/80 py-16 mt-20" data-testid="footer">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="h-5 w-5 text-accent" />
              <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wide">
                {siteName.includes('\u2122') ? <BrandName /> : siteName}
              </h3>
            </div>
            {config.brand_tagline && (
              <p className="text-sm leading-relaxed text-white/60">{config.brand_tagline}</p>
            )}
            {config.legal_name && (
              <p className="text-xs mt-4 text-white/40">{config.legal_name}</p>
            )}
            {hasSocial && (
              <div className="mt-6" data-testid="footer-social">
                <h4 className="font-heading font-bold mb-3 text-white text-sm uppercase tracking-wider">Follow Us</h4>
                <div className="flex items-center gap-3">
                  {social.facebook_url && (
                    <a
                      href={social.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-accent hover:bg-white/5 transition-colors"
                      data-testid="footer-social-facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                  )}
                  {social.instagram_url && (
                    <a
                      href={social.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-accent hover:bg-white/5 transition-colors"
                      data-testid="footer-social-instagram"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
          {columns.map((col, ci) => (
            <div key={ci} data-testid={`footer-col-${ci}`}>
              <h4 className="font-heading font-bold mb-4 text-white text-sm uppercase tracking-wider">{col.title}</h4>
              <ul className="space-y-2 text-sm">
                {(col.links || []).map((link, li) => renderLink(link, li))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-xs text-white/40">
          {config.copyright_text && <p>{config.copyright_text}</p>}
          {config.fine_print && <p className="mt-2 md:mt-0">{config.fine_print}</p>}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
